<script setup lang="ts">
import type { ApiError } from '@/api/client';
import { userMessage } from '@/api/errors';
import { t } from '@/i18n';

/*
 * A game that would not load, and the two ways out of it.
 *
 * Identical, all 22 lines of it, in `GameView` and `HostGameView` — which is unsurprising: both
 * ask the store for the same game and get the same failure.
 *
 * **`NOT_FOUND` gets its own heading.** Everything else is "we could not load this", which invites
 * a retry; a code that does not exist never will, so offering only Try Again would be a loop. The
 * body sentence still comes from `userMessage`, because the API knows more about *why* than the
 * status does.
 *
 * Both ways out are offered on purpose. Try Again is for the dropped connection, Enter A Code is
 * for the mistyped link — and a reader who cannot tell which happened should not have to guess
 * before there is a button to press.
 */
defineProps<{ error: Pick<ApiError, 'code' | 'message'> }>();

const emit = defineEmits<{ retry: [] }>();
</script>

<template>
  <div>
    <h1 class="text-2xl font-semibold">
      {{ error.code === 'NOT_FOUND' ? t('game.notFound') : t('game.failed') }}
    </h1>
    <p class="mt-2 max-w-prose text-ink-muted">
      {{ userMessage(error.code, error.message) }}
    </p>
    <div class="mt-6 flex flex-wrap gap-3">
      <button
        type="button"
        class="rounded bg-accent px-4 py-2 font-medium text-on-accent hover:bg-accent-strong"
        @click="emit('retry')"
      >
        {{ t('game.tryAgain') }}
      </button>
      <RouterLink
        to="/join"
        class="rounded border border-accent px-4 py-2 font-medium hover:bg-fill"
      >
        {{ t('game.enterCode') }}
      </RouterLink>
    </div>
  </div>
</template>
